import { useEffect, useState } from 'react'

/**
 * Three Browse viewing styles:
 *
 * - `list`        — info-dense rows (title, author, description excerpt). Optimized for "find
 *                   that specific build." Existing `BrowseProjectCard`.
 * - `card`        — uniform grid of image-fills-card tiles; title + author appear on hover.
 *                   "Card view but more columns." Built on `BrowseImageCard`.
 * - `inspiration` — variable-size bento grid of image-fills-card tiles; just pictures until
 *                   hover. "For inspiration." Same primitive as `card`, sized via the
 *                   mosaic-scoring algorithm so popular / recent / built-out projects get
 *                   bigger cells.
 */
export type BrowseMode = 'list' | 'card' | 'inspiration'

const STORAGE_KEY = 'todone.browse.mode'
const DEFAULT_MODE: BrowseMode = 'inspiration'
const ALL_MODES: readonly BrowseMode[] = ['list', 'card', 'inspiration']

const readStoredMode = (): BrowseMode => {
  if (typeof window === 'undefined') return DEFAULT_MODE
  const stored = window.localStorage.getItem(STORAGE_KEY) as BrowseMode | null
  return stored && ALL_MODES.includes(stored) ? stored : DEFAULT_MODE
}

/**
 * Persisted Browse view mode. Default is inspiration (just-pictures, variable-size). Persists
 * per browser via localStorage; no server-side preference yet.
 */
export const useBrowseMode = (): [BrowseMode, (next: BrowseMode) => void] => {
  const [mode, setMode] = useState<BrowseMode>(readStoredMode)

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_KEY, mode)
  }, [mode])

  return [mode, setMode]
}
